import React from 'react';
import {createStyles, Theme, withStyles, WithStyles} from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import {Formik, FormikValues} from "formik";
import {useActions} from "../../../../../actions";
import GiftorDocuments from "../../../../../components/ClientNewInvitation/Giftor/GiftorDocuments";
import { fields } from "../../../Kids/util";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../reducers";
import { isDebug, sleep, va } from "../../../../../utils/commonUtils";
import { ClientDocumentType, REQUIRED_FIELD } from "../../../../../constants";
import * as yup from "yup";
import * as ClientAction from "../../../../../actions/client";

const styles = (theme: Theme) =>
        createStyles({
            root: {
                margin: 0,
                padding: theme.spacing(2),
            },
            closeButton: {
                position: 'absolute',
                right: "10px",
                top: '16px',
                color: theme.palette.grey[500],
            },
        });

export interface DialogTitleProps extends WithStyles<typeof styles> {
    id: string;
    children: React.ReactNode;
    onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const {children, classes, onClose, ...other} = props;
    return (
            <MuiDialogTitle disableTypography className={classes.root} {...other}>
                <Typography variant="body1" component="strong">{children}</Typography>
                {onClose ? (
                        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                            <CloseIcon/>
                        </IconButton>
                ) : null}
            </MuiDialogTitle>
    );
});

const DialogContent = withStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(2),
    },
}))(MuiDialogContent);

type RequestModificationProps = {
    enrollmentId: string;
    handleClose: Function | any;
    open: boolean;
    refreshList?: Function
}

export default function RequestDocuments(props: RequestModificationProps) {
    const [initVal, setInitVal] = React.useState<any>({});
    const clientActions = useActions(ClientAction);
    const profileDetail = useSelector((state: RootState) => state.profile);
    const {enrollmentId, handleClose, open, refreshList} = props;
    const [cpID, setCPID] = React.useState<string>("");
    const debugMode = isDebug()

    React.useEffect(() => {

        if(profileDetail && profileDetail.client && profileDetail.client.clientPolicies && profileDetail.client.clientPolicies.length > 0 && profileDetail.client.clientPolicies[0]) {
            const clientPolicies = profileDetail.client.clientPolicies[0];
            setCPID(clientPolicies.id || "")
            extractProfileData(clientPolicies).then(() => {})
        }
    }, [profileDetail]);

    const validate = (() => {
        let keyValidate = {};

        keyValidate[fields.documentType] = yup.string().required(REQUIRED_FIELD);
        keyValidate[fields.identityDocs] = yup.array().min(2, REQUIRED_FIELD).required(REQUIRED_FIELD);

        keyValidate[fields.taxDocumentType] = yup.string().required(REQUIRED_FIELD);
        keyValidate[fields.taxDocument] = yup.array().min(2, REQUIRED_FIELD).required(REQUIRED_FIELD);

        return keyValidate;
    });

    const onRequestModificationSubmit = async (values: FormikValues, {setSubmitting}) => {
        setSubmitting(true);
        await clientActions.updateClientDocument(values, cpID);
        setSubmitting(false);
        if (typeof refreshList == 'function') { refreshList() }
        handleClose()
    };

    const extractProfileData = async (clientPolicies) => {
        const initialFormValues = {
            [fields.identityDocs]: [],
            [fields.taxDocument]: [],
        };

        await extractGiftorValues(initialFormValues, clientPolicies);
        setInitVal(initialFormValues);
    }

    const extractGiftorValues = async (initialFormValues: { [p: string]: never[] }, clientPolicy) => {
            const identityDocType = clientPolicy.identityType;

            if (clientPolicy.clientDocuments) {
                const identity = clientPolicy.clientDocuments.filter((doc: any) => {
                    // we need to make array more than 1 verification Document option Exist
                    return doc.documentType === ClientDocumentType.IDENTITY_DOCUMENT;
                });


                const taxDocs = clientPolicy.clientDocuments.filter((doc: any) => {
                    // we need to make array more than 1 verification taxDocs option Exist
                    const allowedDocs = ["Tax Returns", "W2’s", "1099’s"];

                    // taxType = doc.documentSubType;

                    return allowedDocs.includes(doc.documentSubType);
                });

                const taxType = va(taxDocs) ? taxDocs[0].documentSubType : '';

                const identityDocs = createDocumentPayload(identity, identityDocType, clientPolicy);
                const taxDocument = createDocumentPayload(taxDocs, taxType, clientPolicy);


                Object.assign(initialFormValues, {
                    [fields.identityDocs]: identityDocs,
                    [fields.documentType]: identityDocType,
                    [fields.taxDocument]: taxDocument,
                    [fields.taxDocumentType]: taxType,
                });
            }
        }

    const createDocumentPayload = (clientDocuments: any, docSubType: string, clientPolicy) => {
        const documents: any = [];
        const generateFrontSideDocPayload = (doc: any, isBackPath: boolean = false) => {
            const documentKey = clientPolicy.identityType === "passport" ?
                    (isBackPath ? "uploadPassportBackGEN1GWT" : "uploadPassportGEN1GWT") :
                    (isBackPath ? "uploadDriversLicenseBackGEN1GWT" : "uploadDriversLicenseGEN1GWT");
            return {
                backPath: isBackPath ? doc.backPath : "",
                docSubName: doc.name,
                document: docSubType,
                documentKey,
                documentSubType: doc.documentSubType,
                documentType: doc.documentType,
                path: isBackPath ? doc.backPath : doc.path,
                variant: isBackPath ? "back" : "front",
            };
        };

        clientDocuments.forEach((doc: any) => {
            switch (doc.documentType) {
                case ClientDocumentType.IDENTITY_DOCUMENT:
                    if (doc.path && !doc.backPath) {
                        documents.push(generateFrontSideDocPayload(doc));
                    }
                    if (doc.backPath) {
                        documents.push(generateFrontSideDocPayload(doc));
                        documents.push(generateFrontSideDocPayload(doc, true));
                    }
                    break;
                case ClientDocumentType.TAX_DOCUMENT:
                    documents.push({
                        docSubName: docSubType,
                        document: doc.documentSubType,
                        // documentSubName: doc.documentSubType,
                        documentSubType: doc.name === "TAX DOCUMENT YEAR 1" ? "YEAR_ONE" : "YEAR_TWO",
                        documentType: doc.documentType,
                        path: doc.path,
                        variant: doc.name === "TAX DOCUMENT YEAR 1" ? "firstYear" : "secondYear",
                    });
            }
        });
        return documents;
    };

    const _validationSchema = validate()
    return (
            <div>
                <Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
                    <DialogTitle id="customized-dialog-title" onClose={handleClose}>
                        Request Documents
                    </DialogTitle>
                    <DialogContent dividers>
                        <Formik
                            initialValues={initVal}
                            enableReinitialize={true}
                            validationSchema={yup.object().shape(_validationSchema)}
                            validateOnChange={true}
                            onSubmit={onRequestModificationSubmit}
                        >
                            {
                                ({  handleSubmit,
                                     isSubmitting,
                                     dirty,
                                     errors,
                                     values
                                 }) => (
                                    <form onSubmit={handleSubmit}>
                                        {debugMode && <pre>{
                                            JSON.stringify({
                                                errors,
                                                values,
                                            }, null, 2)
                                        }</pre>}
                                        <GiftorDocuments enrollmentID={enrollmentId} isGWTGen2={true} />
                                        <Button variant="contained" className="floatRight" color="primary"
                                                type={'submit'} disabled={!dirty || isSubmitting}>
                                            Send
                                        </Button>
                                    </form>
                                )
                            }
                        </Formik>

                    </DialogContent>
                </Dialog>
            </div>
    );
}
